import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  Button,
  Container,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Emicalculator from "../assets/images/emi-calculator.webp";
import { Link } from "react-router-dom";
import { paths } from "../routes/path";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e3a8a", // Deep blue
    },
    secondary: {
      main: "#00cc87", // Red for EMI amount
    },
  },
});

const EMICalculator = ({ rateofInterest }) => {
  const [amount, setAmount] = useState(25000);
  const [period, setPeriod] = useState(6);
  const [interestRate, setInterestRate] = useState(
    rateofInterest ? rateofInterest : 10
  ); // Changed default to 10%
  const [emi, setEmi] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Calculate EMI
  const calculateEMI = () => {
    const principal = amount;
    const monthlyRate = interestRate / 12 / 100;
    const numberOfPayments = period;

    if (monthlyRate === 0) {
      return principal / numberOfPayments;
    }

    const emiAmount =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    return emiAmount;
  };

  useEffect(() => {
    const calculatedEMI = calculateEMI();
    setEmi(calculatedEMI);
  }, [amount, period, interestRate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          margin: { xs: "20px 10px", md: "40px 20px 20px 20px" },
          padding: { xs: 2.5, md: 4 },
          backgroundImage: {
            xs: `linear-gradient(135deg, rgba(4, 54, 148, 0.95), rgba(2, 101, 254, 0.92)), url(${Emicalculator})`,
            md: `url(${Emicalculator})`,
          },
          backgroundSize: { xs: "cover", md: "100% 100%" },
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#043694",
          borderRadius: { xs: "16px", md: "0" },
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems="center"
            justifyContent="space-between"
            spacing={4}
            sx={{ maxWidth: "100%" }}
          >
            {/* Left Side - Features */}
            <Box
              sx={{ width: { xs: "100%", md: "50%" }, mb: { xs: 4, md: 0 } }}
            >
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "1.75rem", md: "3.5rem" },
                  marginBottom: { xs: 1.5, md: 4 },
                  lineHeight: 1.1,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                EMI Calculator
              </Typography>

              <Box
                sx={{
                  color: "white",
                  fontSize: { xs: "0.9rem", md: "1.1rem" },
                  lineHeight: 1,
                }}
              >
                <Typography
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Competitive interest rates that are easy on the pocket.
                </Typography>
                <Typography
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Comfortable options for repayment for customers.
                </Typography>
                <Typography
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Easy digital application process with round-the-clock service
                  and support.
                </Typography>
                <Typography
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Easy eligibility criteria for interest.
                </Typography>
                <Typography
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Lowest EMIs per month.
                </Typography>
                <Typography
                  sx={{
                    mb: { xs: 1.5, md: 4 },
                    fontSize: isMobile ? "14px" : "18px",
                    color: "#fff",
                    lineHeight: 1.3,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                  }}
                >
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#00cc87",
                    }}
                  >
                    |
                  </span>{" "}
                  Easy foreclosure of loan.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 2,
                  flexDirection: { xs: "row", md: "row" },
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
              >
                <Link
                  to={paths.dashboard.contactUs}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: { xs: "#00cc87", md: "transparent" },
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      px: { xs: 4, md: 5 },
                      py: { xs: 0.8, md: 0.5 },
                      border: { xs: "none", md: "1px solid white" },
                      mt: { xs: 0, md: 3 },
                      borderRadius: "8px",
                      width: { xs: "auto", md: "auto" },
                      "&:hover": {
                        backgroundColor: "#0265fe",
                      },
                    }}
                  >
                    Apply Now
                  </Button>
                </Link>
              </Box>
            </Box>

            {/* Right Side - Calculator */}
            <Box sx={{ width: { xs: "100%", md: "50%" } }}>
              <Card
                sx={{
                  borderRadius: "20px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  padding: { xs: 1, md: 3 },
                  marginTop: { xs: -1, md: 0 },
                }}
              >
                <CardContent>
                  {/* Amount Slider */}
                  <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",

                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: "row", md: "row" },
                        mb: { xs: 0, md: 0 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          mb: { xs: 0, md: 0 },
                        }}
                      >
                        Amount
                      </Typography>

                      <TextField
                        value={amount}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (!isNaN(value)) setAmount(value);
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            textAlign: "center",
                            width: "120px",
                            height: "32px",
                            padding: "0",
                            fontSize: { xs: "12px", md: "16px" },
                            "& input": {
                              textAlign: "center",
                              padding: "4px 0",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Slider
                      value={amount}
                      onChange={(e, newValue) => setAmount(newValue)}
                      min={10000}
                      max={4000000}
                      step={1000}
                      sx={{
                        height: { xs: 6, md: 10 },
                        borderRadius: 5,
                        py: { xs: 0, md: 2 },
                        "& .MuiSlider-thumb": {
                          width: 24,
                          height: 24,
                          backgroundColor: "#fff",
                          border: "3px solid #3b82f6",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                          transition: "all 0.2s ease",
                          "&:hover, &.Mui-focusVisible": {
                            boxShadow: "0 0 0 6px rgba(59, 130, 246, 0.2)",
                          },
                          "&.Mui-active": {
                            boxShadow: "0 0 0 10px rgba(59, 130, 246, 0.3)",
                          },
                        },
                        "& .MuiSlider-track": {
                          background: "#0265fe",
                          border: "none",
                          height: { xs: 6, md: 10 },
                          borderRadius: 5,
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "#e5e7eb",
                          opacity: 1,
                          height: { xs: 6, md: 10 },
                          borderRadius: 5,
                        },
                        "& .MuiSlider-valueLabel": {
                          backgroundColor: "#3b82f6",
                          color: "#fff",
                          borderRadius: 12,
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                          "&:before": {
                            content: '""',
                            position: "absolute",
                            bottom: -4,
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            width: 8,
                            height: 8,
                            backgroundColor: "#3b82f6",
                          },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: { xs: 0.5, md: 0 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        {amount}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        40Lac
                      </Typography>
                    </Box>
                  </Box>

                  {/* Period Slider */}
                  <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: "row", md: "row" },
                        mb: { xs: 0, md: 0 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          mb: { xs: 0, md: 0 },
                        }}
                      >
                        Period
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          padding: "0 15px",
                          borderRadius: "24px",
                          border: "1px solid #e5e7eb",
                          width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {period}
                      </Typography>
                    </Box>
                    <Slider
                      value={period}
                      onChange={(e, newValue) => setPeriod(newValue)}
                      min={6}
                      max={180}
                      step={1}
                      sx={{
                        height: { xs: 6, md: 10 },
                        borderRadius: 5,
                        py: { xs: 0, md: 2 },
                        "& .MuiSlider-thumb": {
                          width: 24,
                          height: 24,
                          backgroundColor: "#6b7280",
                          border: "3px solid white",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.2s ease",
                          "&:hover, &.Mui-focusVisible": {
                            boxShadow: "0 0 0 6px rgba(107, 114, 128, 0.1)",
                          },
                          "&.Mui-active": {
                            boxShadow: "0 0 0 10px rgba(107, 114, 128, 0.2)",
                          },
                        },
                        "& .MuiSlider-track": {
                          backgroundColor: "#6b7280",
                          height: { xs: 6, md: 10 },
                          border: "none",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "#e5e7eb",
                          height: { xs: 6, md: 10 },
                          opacity: 1,
                        },
                        "& .MuiSlider-valueLabel": {
                          backgroundColor: "#6b7280",
                          color: "white",
                          borderRadius: 8,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: "500",
                          "&:before": {
                            content: '""',
                            position: "absolute",
                            bottom: -4,
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            width: 8,
                            height: 8,
                            backgroundColor: "#6b7280",
                          },
                        },
                      }}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value} months`}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: { xs: 0, md: 1 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        6 months
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        180 months
                      </Typography>
                    </Box>
                  </Box>

                  {/* Interest Rate Slider */}
                  <Box sx={{ mb: { xs: 0.5, md: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: "row", md: "row" },
                        mb: { xs: 0, md: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          mb: { xs: 0, md: 0 },
                        }}
                      >
                        Interest
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#606060",
                          fontSize: { xs: "10px", md: "16px" },
                          padding: "0 15px",
                          borderRadius: "24px",
                          border: "1px solid #e5e7eb",
                          width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {interestRate}%
                      </Typography>
                    </Box>
                    <Slider
                      value={interestRate}
                      onChange={(e, newValue) => setInterestRate(newValue)}
                      min={10} // Changed from 8 to 10
                      max={20} // Changed from 36 to 20
                      step={0.1} // Changed to allow decimal increments
                      sx={{
                        height: { xs: 6, md: 10 },
                        borderRadius: 5,
                        py: { xs: 0, md: 2 },
                        "& .MuiSlider-thumb": {
                          width: 24,
                          height: 24,
                          backgroundColor: "#6b7280",
                          border: "3px solid white",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.2s ease",
                          "&:hover, &.Mui-focusVisible": {
                            boxShadow: "0 0 0 6px rgba(107, 114, 128, 0.1)",
                          },
                          "&.Mui-active": {
                            boxShadow: "0 0 0 10px rgba(107, 114, 128, 0.2)",
                          },
                        },
                        "& .MuiSlider-track": {
                          backgroundColor: "#6b7280",
                          height: { xs: 6, md: 10 },
                          border: "none",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "#e5e7eb",
                          height: { xs: 6, md: 10 },
                          opacity: 1,
                        },
                        "& .MuiSlider-valueLabel": {
                          backgroundColor: "#6b7280",
                          color: "white",
                          borderRadius: 8,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: "500",
                          "&:before": {
                            content: '""',
                            position: "absolute",
                            bottom: -4,
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            width: 8,
                            height: 8,
                            backgroundColor: "#6b7280",
                          },
                        },
                      }}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: { xs: 0.5, md: 0 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        {interestRate}%
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        20%
                      </Typography>
                    </Box>
                  </Box>

                  {/* EMI Result */}
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#00cc87",
                        borderRadius: "15px",
                        padding: { xs: 0.5, md: 1.5 },
                        textAlign: "center",
                        width: { xs: "120px", md: "200px" },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: { xs: "0.9rem", md: "1.5rem" },
                        }}
                      >
                        {formatCurrency(emi)}/-
                      </Typography>
                    </Paper>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      color: "#606060",
                      textAlign: "center",
                      fontSize: { xs: "12px", md: "16px" },
                    }}
                  >
                    You will pay an EMI of only {formatCurrency(emi)}/- monthly
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default EMICalculator;
